import { View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import isMobile from 'src/_shared/utils/isMobile';
import { useStore } from 'src/contexts/StoreContext';
import { ILookupModel } from 'src/store/_models/lookup';
import {
  DYNAMIC_MODAL_ACTIONS,
  DYNAMIC_MODAL_VIEWS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ICON_NAMES } from '../../../../icon/icon.helper';
import { useToolbarLogic } from '../../../../toolbar/Toolbar.logic';
import { logError } from 'src/_shared/logger';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '../../../../focus-provider/FocusProvider.constants';
import { DynamicModalProps } from '../../DynamicModal.types';
import { styles } from './ModalQualityAlertSuggestions.styles';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { KEEP_ANYWAY } from '../ModalQualityAlert.constants';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { STEPS } from 'src/editor-v3/components/upload-picker/UploadSteps';
import { isHappyCase } from 'src/helpers/group.helper';
import { QuaternaryOnLightButton } from 'src/editor-v3/components/buttons/quaternary-on-light-button/QuaternaryOnLightButton';

enum QualityOptionType {
  CHANGE_PHOTO = 'ChangePhoto',
  REMOVE_ZOOM = 'RemoveZoom',
  CHANGE_FORMAT = 'ChangeFormat',
  DELETE_PHOTO = 'DeleteItem',
}

export const ModalQualityAlertSuggestions = ({
  modalQualitySuggestions,
  qualityAlertUserImage,
  modalQualityOpenPoint,
  actionCallback,
}: Pick<
  DynamicModalProps['dynamicModalLayer'],
  'modalQualitySuggestions' | 'qualityAlertUserImage' | 'modalQualityOpenPoint' | 'actionCallback'
>) => {
  const {
    toolbarDesktop: { handleToggleClose, setActiveLookup },
    project: { activeProduct, goBack, setActiveLoopkup, completeRemoveProduct, activeGroup, activeProject },
    dynamicModal: { closeModal: _closeModal },
  } = useStore();

  if (!activeProduct) return <></>;
  const route = useTypedRoute<ROUTE_NAMES.Edit>();
  const { productId, ...filteredParams } = route?.params ?? {};
  const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);
  const isEdit = [ROUTE_NAMES.Edit].includes(route?.name as ROUTE_NAMES);
  const navigation = useTypedNavigation<ROUTE_NAMES.Edit>();
  const { openBottomSheetLookupWithOptions, closeModal, openModal } = useToolbarLogic({});
  const { i18n } = useStore();
  const productType = activeProduct.maxUserImages > 1 ? 'multi_product' : 'single_product';

  const onPressChangePhoto = async () => {
    // open upload modal
    if (!newRoute && !isEdit) {
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
        isReplacingPhoto: true,
        uploadMultiSourceCallback: async params => {
          try {
            if (!!qualityAlertUserImage && !!params?.files) {
              await qualityAlertUserImage.replaceImage({ files: params.files });
              amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
                mode: productType,
                source: modalQualityOpenPoint,
                option: 'replace_photo',
              });
              actionCallback(DYNAMIC_MODAL_ACTIONS.REPLACE_PHOTO);
            }
          } catch (error) {
            logError(error, params);
          }

          closeModal();
        },
      });
    } else {
      amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
        mode: productType,
        source: modalQualityOpenPoint,
        option: 'replace_photo',
      });
      goBack();
      _closeModal();
      const id = await activeProject?.getDefaultProductIdByGroup(activeGroup);
      const isHappyCaseGroup = !!activeGroup?.code && isHappyCase(activeGroup?.code);
      navigation.navigate(ROUTE_NAMES.Upload, {
        skipConfigurationStep: !isHappyCaseGroup ? STEPS.Format : STEPS.BoxStyle,
        skipStepValue: id || activeGroup?.currectProductConfigurationId,
        replacePhoto: qualityAlertUserImage?.id,
        ...filteredParams,
      });
    }
  };

  const onPressRemoveZoom = () => {
    qualityAlertUserImage?.restore({ keepFilter: true, keepAdjustments: true }).then(() => {
      amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
        mode: productType,
        source: modalQualityOpenPoint,
        option: 'remove_zoom',
      });
      closeModal();
      actionCallback(DYNAMIC_MODAL_ACTIONS.REMOVE_ZOOM);
    });
  };

  const onPressChangeLayout = async () => {
    goBack();
    _closeModal();
    if (!newRoute && !isEdit) {
      if (isMobile()) {
        const lookup = activeProduct.lookups.filter((lookup: ILookupModel) => lookup.code == 'format')[0];
        openBottomSheetLookupWithOptions(lookup);
      } else {
        handleToggleClose({ onlyClose: false, id: 'format' });
        setActiveLookup('format');
      }
    } else {
      if (isEdit) {
        activeGroup?.deselectAllProducts();
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else {
          navigation.navigate(ROUTE_NAMES.EditorNew, filteredParams);
        }
      }
      qualityAlertUserImage?.product?.setIsSelected(true);
      setActiveLoopkup(LookupCodes.FORMAT);
    }

    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
      mode: productType,
      source: modalQualityOpenPoint,
      option: 'choose_format',
    });
    closeModal();
    actionCallback(DYNAMIC_MODAL_ACTIONS.CHOOSE_FORMAT);
  };

  const onPressDelete = async () => {
    openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteProduct,
      productToDelete: qualityAlertUserImage?.product,
      onPressDeleteProduct: () => {
        completeRemoveProduct();
      },
    });
  };

  const qualityOptionType = {
    [QualityOptionType.CHANGE_PHOTO]: {
      clickHandler: onPressChangePhoto,
      iconName: ICON_NAMES.Refresh,
      description: i18n.t(`modal-quality-alert:change-photo`),
    },
    [QualityOptionType.REMOVE_ZOOM]: {
      clickHandler: onPressRemoveZoom,
      iconName: ICON_NAMES.Crop,
      description: i18n.t(`modal-quality-alert:remove-zoom`),
    },
    [QualityOptionType.CHANGE_FORMAT]: {
      clickHandler: onPressChangeLayout,
      iconName: ICON_NAMES.Ruler,
      description: i18n.t(`modal-quality-alert:change-format`),
    },
    [QualityOptionType.DELETE_PHOTO]: {
      clickHandler: onPressDelete,
      iconName: ICON_NAMES.Delete,
      description: i18n.t(`modal-quality-alert:delete`),
    },
  };

  const modalQualityOptions = modalQualitySuggestions?.options?.filter((item: string) => item !== KEEP_ANYWAY) ?? [];

  return (
    <View style={[styles.optionsContainer, { gap: 8 }]}>
      {modalQualityOptions.map((option, index) => {
        return (
          <QuaternaryOnLightButton
            onPress={async () => qualityOptionType[option as QualityOptionType].clickHandler()}
            key={option}
            focusId={modalTestID(TestIDs.QUALITY_ALERT, 'quality-option', String(index))}
            focusOrder={index + 10}
            focusGroup={FocusGroups.MODAL}
            text={qualityOptionType[option as QualityOptionType].description}
            textStyle={styles.option}
            innerStyle={styles.qualityOption}
            iconName={qualityOptionType[option as QualityOptionType].iconName}
            iconStyle={styles.qualityIcon}
            size={BUTTON_SIZES.REGULAR}
            type={BUTTON_TYPES.ICON_LEFT}
            layout={BUTTON_LAYOUTS.FILL}
            accessible={true}
            accessibilityLabel={qualityOptionType[option as QualityOptionType].description}
            accessibilityRole="button"
          />
        );
      })}
    </View>
  );
};
